<script setup lang="ts">
/**
 * StatusPill — title-row + list-row status badge.
 *
 * Two APIs (kept together intentionally per Phase 4.6 D-06 — extend, don't fork):
 *
 *   1. Legacy `status` prop (already used by `AppointmentsView`'s list rows and
 *      `MagicLinkAppointmentView`). Pass a raw API status string ("pending",
 *      "running", "late", "completed", "cancelled", etc.) and an optional
 *      `label` override. Class palette is the project's Tailwind semantic
 *      tokens (`bg-scheduled-bg` / `text-scheduled`, `bg-warning-bg` /
 *      `text-warning`, etc.).
 *
 *   2. New `pillKey` prop (Phase 4.6 — unified appointment detail). Pass a
 *      `StatusPillKey` (`'pending' | 'enroute' | 'arrived' | 'late' |
 *      'completed' | 'completed-unpaid' | 'cancelled'`) and the component
 *      reads `STATUS_PILL_COPY[pillKey]` from `@sera/core` to render the
 *      sketch 022 contract — coloured class + leading FA icon + sentence-case
 *      text. The mapping from raw API status → pill key lives in
 *      `mapStatusToPillKey()` at the parent so this component never reads
 *      raw `appointment.status` strings (D-43 single-source-of-truth rule).
 *
 * When both props are provided, `pillKey` wins. The slot still overrides the
 * rendered text body so callers that want a custom inner can pass markup —
 * but in nearly all cases the copy from `STATUS_PILL_COPY` is the right
 * answer and matches the canonical visual contract.
 *
 * DESIGN-03: PassThrough / utility classes only — no scoped styles, no
 * inline styles. Class strings are full literals so the Tailwind scanner
 * picks them up.
 */
import { computed } from 'vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { STATUS_PILL_COPY, type StatusPillKey } from '@sera/core';

interface Props {
  // Legacy API — raw API status string (list-row callers). One of the keys
  // in STATUS_CLASSES below; unknown values fall back to muted canvas.
  status?: string;
  // Optional human label override (legacy API). When provided, replaces the
  // auto-derived "Preferred Assignment"-style capitalisation.
  label?: string;
  // New API (Phase 4.6 D-06): pill-key from `mapStatusToPillKey()`. When
  // present, drives class + icon + text from `STATUS_PILL_COPY`.
  pillKey?: StatusPillKey;
}

const props = withDefaults(defineProps<Props>(), {
  status: '',
  label: '',
  pillKey: undefined,
});

// ── Legacy `status` palette ────────────────────────────────────────────────
// Full-literal class strings — never interpolated (CLAUDE.md constraint).
// Color semantics: completed = green; scheduled / en-route / working = blue
// ramp (light / medium / strong); running-late = amber; cancelled = muted.
const STATUS_CLASSES: Record<string, string> = {
  pending: 'bg-scheduled-bg text-scheduled',
  assigned: 'bg-scheduled-bg text-scheduled',
  preferred_assignment: 'bg-scheduled-bg text-scheduled',
  accepted: 'bg-scheduled-bg text-scheduled',
  running: 'bg-enroute-bg text-enroute',
  // `working` is a virtual status — not returned by the API yet. The split
  // between en-route and working is keyed on arrival_time once the consumer
  // datatable exposes it; pre-wired here so callers can pass status="working".
  working: 'bg-working-bg text-working',
  late: 'bg-warning-bg text-warning',
  completed: 'bg-success-bg text-success',
  cancelled: 'bg-canvas text-muted',
  canceled: 'bg-canvas text-muted',
  quote_denied: 'bg-canvas text-muted',
};

// ── New `pillKey` palette (sketch 022 D-06) ────────────────────────────────
// One full-literal mapping per pill key. Mirrors `STATUS_PILL_COPY` cls
// semantics from `@sera/core` (`badge-info`, `badge-success`, `badge-warning`,
// neutral) onto the project's Tailwind semantic tokens. Kept here (not in
// the helper module) because the *colour* tokens are a UI concern and the
// `STATUS_PILL_COPY` cls strings are sketch-vocabulary only.
const PILL_KEY_CLASSES: Record<StatusPillKey, string> = {
  pending: 'bg-scheduled-bg text-scheduled',
  assigned: 'bg-scheduled-bg text-scheduled',
  accepted: 'bg-scheduled-bg text-scheduled',
  enroute: 'bg-enroute-bg text-enroute',
  arrived: 'bg-success-bg text-success',
  late: 'bg-warning-bg text-warning',
  completed: 'bg-success-bg text-success',
  'completed-unpaid': 'bg-warning-bg text-warning',
  cancelled: 'bg-canvas text-muted',
  // 5.5 D-12 / D-18: muted/gray semantic for expired quotes.
  expired: 'bg-canvas text-muted',
  // TC-708 D-13: signed quotes, green like the tech app's Accepted chip.
  'quote-accepted': 'bg-success-bg text-success',
  // 5.2 D-21: invoice + plan-row pill colours per UI-SPEC § Color taxonomy.
  // paid = green; open/scheduled = sky/info; partial = amber; past-due = red.
  paid: 'bg-success-bg text-success',
  open: 'bg-scheduled-bg text-scheduled',
  partial: 'bg-warning-bg text-warning',
  // 260618-esd: current-draw shares the amber/warning palette with partial but
  // is a distinct key (own copy + bullseye icon). Full-literal string so the
  // Tailwind scanner picks it up; required member of the StatusPillKey record.
  'current-draw': 'bg-warning-bg text-warning',
  'past-due': 'bg-danger-bg text-danger',
  scheduled: 'bg-scheduled-bg text-scheduled',
};

const usePillKey = computed<boolean>(() => props.pillKey !== undefined);

const pillClass = computed<string>(() => {
  if (usePillKey.value && props.pillKey) {
    // Mirror the legacy fallback. PILL_KEY_CLASSES is keyed to StatusPillKey
    // at compile time, but the prop can arrive as a cast/drifted value, in
    // which case the lookup is undefined and the pill loses its colour.
    return PILL_KEY_CLASSES[props.pillKey] ?? 'bg-canvas text-muted';
  }
  return STATUS_CLASSES[props.status] ?? 'bg-canvas text-muted';
});

// The `pillKey` copy from `STATUS_PILL_COPY` carries an FA icon name (e.g.
// "fa-truck"). The icon registration plugin already adds the solid-svg-icons
// pack globally, so we can render via the FontAwesomeIcon string-array
// syntax `['fas', 'truck']` without importing each icon here. This keeps
// libs/ui dependency-light.
const pillIcon = computed<['fas', string] | null>(() => {
  if (!usePillKey.value || !props.pillKey) return null;
  const raw = STATUS_PILL_COPY[props.pillKey]?.icon ?? '';
  if (!raw) return null;
  // Normalise "fa-truck" → "truck"; tolerate already-bare names.
  const iconName = raw.replace(/^fa-/, '');
  return ['fas', iconName];
});

const pillLabel = computed<string>(() => {
  if (usePillKey.value && props.pillKey) {
    // STATUS_PILL_COPY lives in @sera/core while PILL_KEY_CLASSES lives here,
    // so the two can drift. Fall back to the raw key rather than '' so a pill
    // is never rendered with a coloured background and no readable text.
    return STATUS_PILL_COPY[props.pillKey]?.text ?? props.pillKey;
  }
  return (
    props.label ||
    props.status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  );
});
</script>

<template>
  <span
    :class="[
      'inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-sm font-medium',
      pillClass,
    ]"
    data-testid="status-pill"
  >
    <FontAwesomeIcon
      v-if="pillIcon"
      :icon="pillIcon"
      class="size-3"
      data-testid="status-pill-icon"
    />
    <slot>{{ pillLabel }}</slot>
  </span>
</template>
